import { map, of } from 'rxjs';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { context } from '@adornis/baseql/server/context.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Company } from '../db/company.js';
import { JobPosition } from '../db/job-position.js';
import { MatchingWeekPermissions, MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication, JobApplicationStatus } from '../db/job-application.js';
import { MatchingScoreCache } from '../db/MatchingScoreCache.js';
import { overlapsInterval } from '../shared/time-slot-overlap.js';
import * as m from '../../localization/out/messages.js';

type MatchingWeekUser = User & { hasPermission(permission: MatchingWeekPermissions, context?: string): boolean };
type StoredInterviewSlot = { _id: string; _class: string; start: Date; end: Date; isNonBlocking: boolean };

function hydrateJobPosition(value: unknown): JobPosition {
  const node = baseqlMetaData.entities.resolveNode(JobPosition._class);
  if (!node) throw new Error(`Could not find entity with name: ${JobPosition._class}`);
  const field = { name: JobPosition._class, type: node.type, arrayDepth: () => 0 };
  return constructValue<JobPosition>(value, field);
}

@Entity()
export class AddInterviewSlotsResult extends AdornisEntity {
  static override _class = 'AddInterviewSlotsResult';

  @Field(() => Int)
  added!: number;

  @Field(() => Int)
  skipped!: number;

  @Field(() => Int)
  removed!: number;
}

export const addInterviewSlots = registerMutation({
  operationName: 'addInterviewSlots',
  type: () => AddInterviewSlotsResult,
  params: [
    { name: 'jobPositionID', type: () => ID },
    { name: 'slots', type: () => [TimeSlot] },
    { name: 'removeUnbookedSlotIDs', type: () => [ID] },
  ],
  resolve: (jobPositionID: string, slots: TimeSlot[], removeUnbookedSlotIDs: string[]) => async gqlFields => {
    if (!context.serverContext) {
      const user = (await context.user()) as Maybe<MatchingWeekUser>;
      if (!user) throw new NotLoggedInError();
      const jobPosition = await getJobPositionByID(jobPositionID)({ _id: 1, companyID: 1 });
      if (!jobPosition?.companyID) throw new Error('Job position not found');
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)) {
        throw new Error('Not authorized for this job position');
      }
    }

    const collection = mongo().collection<{ _id: string; timeSlots?: StoredInterviewSlot[] }>(
      JobPosition._collectionName,
    );
    const position = await collection.findOne({ _id: jobPositionID });
    if (!position) throw new Error('Job position not found');

    const normalizedSlots = slots.map(slot => {
      const start = slot.start == null ? undefined : new Date(slot.start);
      const end = slot.end == null ? undefined : new Date(slot.end);
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start)
        throw new Error(m.interview_slots_invalid({}, { locale: 'de' }));
      return { _id: A.getGloballyUniqueID(), _class: TimeSlot._class, start, end, isNonBlocking: false };
    });
    const keptUnbooked = (position.timeSlots ?? []).filter(slot => !removeUnbookedSlotIDs.includes(slot._id));
    const applicationCollection = mongo().collection(JobApplication._collectionName);
    const bookedApplications = await applicationCollection
      .find(
        {
          jobPositionId: jobPositionID,
          status: JobApplicationStatus.BOOKED,
          interviewStart: { $exists: true, $ne: null },
          interviewEnd: { $exists: true, $ne: null },
        },
        { projection: { interviewStart: 1, interviewEnd: 1 } },
      )
      .toArray();
    const newSlots = normalizedSlots.reduce<StoredInterviewSlot[]>((added, slot) => {
      if (
        bookedApplications.some(application =>
          overlapsInterval(slot, { start: application.interviewStart, end: application.interviewEnd }),
        ) ||
        keptUnbooked.some(kept => overlapsInterval(slot, kept)) ||
        added.some(previous => overlapsInterval(slot, previous))
      )
        return added;
      return [...added, slot];
    }, []);

    if (removeUnbookedSlotIDs.length > 0) {
      const accepted = await applicationCollection.countDocuments({
        jobPositionId: jobPositionID,
        status: JobApplicationStatus.ACCEPTED,
      });
      if (keptUnbooked.length + newSlots.length < accepted)
        throw new Error(m.interview_slots_capacity_error({ accepted }, { locale: 'de' }));
    }

    // Filter the live array: a concurrent booking's $pull must never be undone.
    const updatedPosition = await collection.findOneAndUpdate(
      { _id: jobPositionID },
      [
        {
          $set: {
            timeSlots: {
              $concatArrays: [
                {
                  $filter: {
                    input: { $ifNull: ['$timeSlots', []] },
                    cond: { $not: { $in: ['$$this._id', removeUnbookedSlotIDs] } },
                  },
                },
                newSlots,
              ],
            },
          },
        },
      ],
      // Use the live preimage to count our removals, excluding slots claimed by concurrent bookings.
      { returnDocument: 'before' },
    );
    if (!updatedPosition) throw new Error('Job position not found');
    await mongo().collection(MatchingScoreCache._collectionName).deleteMany({ jobPositionID });
    return new AddInterviewSlotsResult({
      added: newSlots.length,
      skipped: normalizedSlots.length - newSlots.length,
      removed: (updatedPosition.timeSlots ?? []).filter(slot => removeUnbookedSlotIDs.includes(slot._id)).length,
    });
  },
});

export const getJobPositionsByCompany = registerQuery({
  operationName: 'getJobPositionsByCompany',
  type: () => [JobPosition],
  params: [{ name: 'companyID', type: () => ID }],
  resolve: (companyID: string) => async (gqlFields: BaseQLSelectionSet<JobPosition>) => {
    const collection = await scopedCollection(JobPosition);
    return collection.find({ companyID: companyID }).project(gqlFields).toArray() as Promise<JobPosition[]>;
  },
});

export const subscribeToJobPositionsByCompany = registerSubscription({
  operationName: 'subscribeToJobPositionsByCompany',
  type: () => [JobPosition],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve: (skip: number, limit: number, filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<JobPosition>) => {
    // TODO (low prio) we're using the search for the companyID filter
    return watchQuery<JobPosition>(
      JobPosition._collectionName,
      { companyID: filter.search },
      { skip, limit, sort: { createdAt: -1 } },
    ).pipe(map(docs => docs.map(hydrateJobPosition)));
  },
});

export const countJobPositionsByCompany = registerQuery({
  operationName: 'countJobPositionsByCompany',
  type: () => Int,
  params: [{ name: 'companyID', type: () => ID }],
  resolve: (companyID: string) => async () => {
    return (await scopedCollection(JobPosition)).countDocuments({ companyID });
  },
});

export const countAllJobPositions = registerQuery({
  operationName: 'countAllJobPositions',
  type: () => Int,
  resolve: () => async () => {
    return (await scopedCollection(JobPosition)).countDocuments();
  },
});

export const getAllJobPositions = registerQuery({
  operationName: 'getAllJobPositions',
  type: () => [JobPosition],
  resolve: (filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<JobPosition>) => {
    const collection = mongo().collection(JobPosition._collectionName);
    const result = collection
      .aggregate([
        { $lookup: { from: Company._collectionName, localField: 'companyID', foreignField: '_id', as: 'company' } },
        { $addFields: { company: { $arrayElemAt: ['$company', 0] } } },
        // Exclude jobs whose company is archived (isActive === false).
        // `$ne: false` keeps backward compat: missing field or true both pass.
        { $match: { ...filter.getMongoFilter(), 'company.isActive': { $ne: false } } },
        { $unset: 'company' },
      ])
      .toArray();
    return result as Promise<JobPosition[]>;
  },
  params: [{ name: 'filter', type: () => AdornisFilter }],
});

export const getJobPositionsByIDs = registerQuery({
  operationName: 'getJobPositionsByIDs',
  type: () => [JobPosition],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) =>
      readEntitiesByIDs(JobPosition, ids, gqlFields),
});

export const subscribeJobPositionsByIDs = registerSubscription({
  operationName: 'subscribeJobPositionsByIDs',
  type: () => [JobPosition],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(JobPosition, ids, gqlFields),
});

export const getAllJobPositionsAll = registerQuery({
  operationName: 'getAllJobPositionsAll',
  type: () => [JobPosition],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) =>
      readAllEntities(JobPosition, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllJobPositions = registerSubscription({
  operationName: 'subscribeAllJobPositions',
  type: () => [JobPosition],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) =>
      watchAllEntities(JobPosition, gqlFields, { skip, limit, excludeIDs }),
});

export const getJobPositionByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getJobPositionsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeJobPositionByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<JobPosition>>(gqlFields: SelSet) =>
    id ? subscribeJobPositionsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
